import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import {
  PaintBucket,
  Phone,
  Mail,
  MapPin,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  ArrowRight,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const footerLinks = {
  services: [
    { name: "Interior Painting", href: "/services#interior-painting" },
    { name: "Exterior Painting", href: "/services#exterior-painting" },
    { name: "Popcorn Ceiling Removal", href: "/services#popcorn-ceiling" },
    { name: "Garage Floor Coatings", href: "/services#garage-floor" },
    { name: "Deck Staining", href: "/services#deck-staining" },
  ],
  company: [
    { name: "About Us", href: "/about" },
    { name: "Our Process", href: "/process" },
    { name: "Portfolio", href: "/portfolio" },
    { name: "Testimonials", href: "/#testimonials" },
  ],
  resources: [
    { name: "Get a Quote", href: "/quote" },
    { name: "Contact Us", href: "/contact" },
    { name: "Service Areas", href: "/service-areas" },
  ],
};

const socialLinks = [
  { icon: Facebook, href: "https://www.facebook.com/profile.php?id=61567415244280", label: "Facebook" },
  { icon: Instagram, href: "https://instagram.com/rollorlando", label: "Instagram" },
  ];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Successfully subscribed to newsletter!");
      setEmail("");
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("This email is already subscribed");
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer data-testid="footer" className="bg-brand-navy text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-12 sm:py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-8 sm:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-brand-orange rounded-sm flex items-center justify-center">
                <PaintBucket className="w-7 h-7 text-white" />
              </div>
              <div className="flex flex-col">
                <span className="font-heading text-2xl font-semibold tracking-tight uppercase">
                  Roll Orlando
                </span>
                <span className="text-xs text-white/60 tracking-widest uppercase">
                  Professional Painting Services
                </span>
              </div>
            </Link>
            <p className="text-white/70 mb-6 max-w-sm">
              Orlando's trusted painting professionals with 10+ years of experience. 
              We transform homes with quality craftsmanship and attention to detail.
            </p>

            {/* Contact Info */}
            <div className="space-y-3">
              <a
                href="tel:+14079494656"
                className="flex items-center gap-3 text-white/80 hover:text-brand-orange transition-colors"
              >
                <Phone className="w-5 h-5" />
                (407) 949-4656
              </a>
              <a
                href="mailto:sales@rollorlando.com"
                className="flex items-center gap-3 text-white/80 hover:text-brand-orange transition-colors"
              >
                <Mail className="w-5 h-5" />
                sales@rollorlando.com
              </a>
              <div className="flex items-start gap-3 text-white/80">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>
                  Orlando, Florida
                  <br />
                  Serving Central FL
                </span>
              </div>
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold uppercase mb-6">
              Services
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-white/70 hover:text-brand-orange transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold uppercase mb-6">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-white/70 hover:text-brand-orange transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-heading text-lg font-semibold uppercase mb-6">
              Newsletter
            </h4>
            <p className="text-white/70 text-sm mb-4">
              Subscribe for painting tips, seasonal offers, and home improvement ideas.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="space-y-3">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-brand-orange"
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="newsletter-submit-btn"
                className="w-full bg-brand-orange text-white font-heading font-semibold uppercase hover:bg-[#d9432a] transition-colors duration-300"
              >
                {isSubmitting ? "Subscribing..." : "Subscribe"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>

            {/* Social Links */}
            <div className="flex gap-3 mt-6">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-10 h-10 bg-white/10 rounded-sm flex items-center justify-center hover:bg-brand-orange hover:text-white transition-all duration-300"
                  >
                    <Icon className="w-5 h-5" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 py-5 sm:py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4">
            <p className="text-white/50 text-xs sm:text-sm">
              © {new Date().getFullYear()} Roll Orlando Painting. All rights
              reserved.
            </p>
            <div className="flex gap-6">
              <Link
                to="/privacy"
                className="text-white/50 text-sm hover:text-white transition-colors"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-white/50 text-sm hover:text-white transition-colors"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
